import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Chip } from '@packages/shared/src/ui/Chip/Chip';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { Slider } from '@packages/shared/src/ui/Slider/Slider';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { FilterRangeSliderFragmentFragmentDoc } from '@packages/gql/generated/shopping/FilterRangeSliderFragmentFragmentDoc';

import type { FilterRangeValue } from '../../types';
import { messages } from './messages';

import { FilterRangeSliderInput } from '../FilterRangeSliderInput/FilterRangeSliderInput';
import { FilterHistogram } from '../FilterHistogram/FilterHistogram';
import type { BasicFilterValueListProps } from '../BasicFilterValueList/BasicFilterValueList';
import { BasicFilterValueList } from '../BasicFilterValueList/BasicFilterValueList';

// TODO: basic filter value list stuff
/* GraphQL */ `
  fragment FilterRangeSliderFragment on SearchRangeFilter {
    displayName
    userPrompt
    validRange {
      min
      max
      unit
    }
    histogram
    groupedValues {
      groupName
    }
    ...BasicFilterValueListFragment
  }
`;

export type FilterRangeSliderProps = {
  maskedData: FragmentType<typeof FilterRangeSliderFragmentFragmentDoc>;
  /** optional step size for the slider
   * @default 1
   */
  step?: number;
  /** previously/preselected range values */
  selectedRange?: FilterRangeValue;
  /** previously/preselected filter values */
  selectedFilterValues?: string[];
  /** Callback fired when the range selection changes */
  onChangeRange?: (value?: FilterRangeValue) => void;
  /** Callback fired when a filter value is toggled */
  onToggleValue?: BasicFilterValueListProps['onToggle'];
  /** Additional styles which should be applied on the container */
  containerClassName?: string;
};

/**
 * Range slider component to display filter range slider controls.
 *
 * @remarks
 * The onChange handler can have a high fire rate so it is advised to not use an expensive response.
 */
export const FilterRangeSlider: FC<FilterRangeSliderProps> = ({
  maskedData,
  onChangeRange,
  onToggleValue,
  selectedRange,
  selectedFilterValues,
  containerClassName,
  step = 1,
}) => {
  const filter = unmask<typeof FilterRangeSliderFragmentFragmentDoc>(maskedData);
  const {
    displayName,
    userPrompt,
    validRange: { min, max, unit },
    histogram,
    groupedValues,
  } = filter;

  const [initialMin, initialMax] = selectedRange?.range ?? [undefined, undefined];

  const [value, setValue] = useState<[number, number]>([initialMin ?? min, initialMax ?? max]);

  const [minInputValue, setMinInputValue] = useState<string>(
    initialMin !== min ? (initialMin?.toString() ?? '') : '',
  );
  const [maxInputValue, setMaxInputValue] = useState<string>(
    initialMax !== max ? (initialMax?.toString() ?? '') : '',
  );
  const [anyInputWasModified, setAnyInputWasModified] = useState(false);

  const { formatMessage } = useIntl();

  const emitCleanRange = ([newMin, newMax]: [number, number]) => {
    onChangeRange?.(
      newMin !== min || newMax !== max ? { unit, range: [newMin, newMax] } : undefined,
    );
  };

  const handleSliderChange = (newValue: number | number[]) => {
    const [currentMin, currentMax] = value;
    const [newMin, newMax] = newValue as number[];

    if (currentMin !== newMin) {
      setMinInputValue(newMin !== min ? newMin.toString() : '');
    }
    if (currentMax !== newMax) {
      setMaxInputValue(newMax !== max ? newMax.toString() : '');
    }

    setValue([newMin, newMax]);
    emitCleanRange([newMin, newMax]);
  };

  const handleInputCommit = (wasModified: boolean) => {
    if (wasModified) {
      setAnyInputWasModified(true);
    }

    const [currentMin, currentMax] = value;

    if (currentMin > currentMax) {
      const [newMin, newMax] = [currentMax, currentMin];

      setAnyInputWasModified(true);
      setMinInputValue(newMin !== min ? newMin.toString() : '');
      setMaxInputValue(newMax !== max ? newMax.toString() : '');

      setValue([newMin, newMax]);
      emitCleanRange([newMin, newMax]);
    } else if (initialMin !== currentMin || initialMax !== currentMax) {
      emitCleanRange(value);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setAnyInputWasModified(false);
    }, 5000);

    return () => clearTimeout(timeout);
  }, [anyInputWasModified]);

  return (
    <div className={`overflow-auto pt-2 pb-1 ${containerClassName}`}>
      <div className="px-2">
        {!!userPrompt && <p className="text-size-body-sm mb-2">{userPrompt}</p>}
        <div className="flex flex-row justify-between">
          <Chip
            label={formatMessage(messages.rangeSliderChipLabel, { value: value[0], unit })}
            variant="filled"
            className="pointer-events-none border-none"
          />
          <Chip
            label={formatMessage(messages.rangeSliderChipLabel, { value: value[1], unit })}
            variant="filled"
            className="pointer-events-none border-none"
          />
        </div>
      </div>
      <div className="flex flex-col gap-2 px-4 py-2">
        {histogram && histogram.length > 1 && (
          <div className="-mb-2.5">
            <FilterHistogram
              selection={{
                start: (value[0] - min) / (max - min),
                end: (value[1] - min) / (max - min),
              }}
              quantities={histogram}
            />
          </div>
        )}
        <Slider
          value={value}
          min={min}
          max={max}
          step={step}
          onValueChange={handleSliderChange}
          aria-label={displayName}
        />
      </div>
      <div className="px-2 pb-2">
        <div className="flex flex-row justify-between">
          <p className="text-size-body-xs text-color-text-darktransparent">
            <FormattedMessage
              key="minValueHint-text"
              {...messages.minValueHint}
              values={{ min, unit }}
            />
          </p>
          <p className="text-size-body-xs text-color-text-darktransparent">
            <FormattedMessage
              key="maxValueHint-text"
              {...messages.maxValueHint}
              values={{ max, unit }}
            />
          </p>
        </div>
        <div className="mt-2 flex flex-row gap-2.5 max-sm:flex-col max-sm:gap-1.5">
          <div className="w-full">
            <FilterRangeSliderInput
              label={formatMessage(messages.rangeSliderLabelFrom)}
              placeholder={`${min}`}
              adornmentLabel={unit}
              rawValue={minInputValue}
              onChangeRaw={setMinInputValue}
              parsedValue={value[0]}
              onChangeParsed={(newValue) => {
                setValue([newValue, value[1]]);
              }}
              validRange={[min, max]}
              onCommit={handleInputCommit}
              rangeBoundary="min"
            />
          </div>
          <div className="w-full">
            <FilterRangeSliderInput
              label={formatMessage(messages.rangeSliderLabelTo)}
              placeholder={`${max}`}
              adornmentLabel={unit}
              rawValue={maxInputValue}
              onChangeRaw={setMaxInputValue}
              parsedValue={value[1]}
              onChangeParsed={(newValue) => {
                setValue([value[0], newValue]);
              }}
              validRange={[min, max]}
              onCommit={handleInputCommit}
              rangeBoundary="max"
            />
          </div>
        </div>
        {anyInputWasModified && (
          <div className="mt-1">
            <Notification
              severity="warning"
              variant="outline"
              className="text-size-body-xs leading-relaxed"
            >
              <FormattedMessage key="inputClampedHint-text" {...messages.inputClampedHint} />
            </Notification>
          </div>
        )}
      </div>
      {groupedValues?.length > 0 && (
        <>
          <Divider flexItem />
          <BasicFilterValueList
            maskedData={filter}
            selectedIds={selectedFilterValues ?? []}
            onToggle={onToggleValue}
          />
        </>
      )}
    </div>
  );
};
